import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormField, Modal } from '@/design-system/components';
import type { LinkCustomMetabaseReportRequest } from '@/lib/sdk/metabase-reports/types';

const linkCustomReportSchema = z.object({
  dashboard_id: z.number({ error: 'Enter the dashboard number' }).int().min(1, 'Enter the dashboard number'),
  title: z.string().trim().min(1, 'Title is required'),
  description: z.string().trim(),
});

type LinkCustomReportFormData = z.infer<typeof linkCustomReportSchema>;

interface LinkCustomReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (request: LinkCustomMetabaseReportRequest) => void;
  isPending: boolean;
}

export function LinkCustomReportDialog({ isOpen, onClose, onSubmit, isPending }: LinkCustomReportDialogProps) {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<LinkCustomReportFormData>({
    resolver: zodResolver(linkCustomReportSchema),
    defaultValues: { title: '', description: '' },
  });

  // Start empty each time it opens, after a link as after a cancel.
  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Link custom report" size="md" testId="mbr-link-dialog">
      <form onSubmit={handleSubmit((data) => onSubmit({ ...data, description: data.description || undefined }))}>
        <VStack spacing={4} align="stretch">
          <Text fontSize="sm" color="text-secondary">
            Shares a hand-built Metabase dashboard publicly and adds it to this organization&apos;s
            Reports page. Grant access to it there. Nothing rebuilds it. Unlinking turns its public
            link off but leaves the dashboard in Metabase.
          </Text>
          <FormField
            label="Metabase dashboard ID"
            name="dashboard_id"
            type="number"
            min={1}
            register={register}
            error={errors.dashboard_id}
            description="The number in the dashboard's URL: /dashboard/<id>"
            required
            testId="mbr-link-dashboard-id"
          />
          <FormField label="Title" name="title" register={register} error={errors.title} required testId="mbr-link-title" />
          <FormField label="Description" name="description" type="textarea" register={register} error={errors.description} testId="mbr-link-description" />
          <HStack justify="flex-end" spacing={3} pt={2}>
            <Button variant="ghost" onClick={onClose} isDisabled={isPending}>Cancel</Button>
            <Button type="submit" colorScheme="brand" isLoading={isPending} data-testid="mbr-link-submit">Link report</Button>
          </HStack>
        </VStack>
      </form>
    </Modal>
  );
}
